import React, { useEffect, useState } from "react";
import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Menu,
} from "@material-tailwind/react";
import { Bars3BottomLeftIcon } from "@heroicons/react/24/solid";
import api from "../api/api";
import { Paginate } from "../components/Paginate";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  products,
  categoriesProducts,
} from "../features/products/productSlice";
import Categories from "../components/product/Categories";
import { decouvreBg } from "../assets/images";

// Helper function to handle errors
const handleError = (error) => {
  toast.error("Un problème est survenu, veuillez contacter le service client");
  console.error(error);
};

function Product() {
  const dispatch = useDispatch();
  const {
    categories: dataCategories,
    products: listProducts,
    totalPages,
    page: currentPage,
  } = useSelector((state) => state.product);

  const [selectedTab, setSelectedTab] = useState("tout");
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loader, setLoader] = useState(true);

  const productLoader = async (page = 1) => {
    try {
      const response = await api.get(`/products?page=${page}`);
      dispatch(products(response.data));
      setLoader(false);
    } catch (error) {
      handleError(error);
    } finally {
      console.log("finally");
    }
  };

  const getCategories = async () => {
    try {
      const response = await api.get("/products/categories");
      dispatch(categoriesProducts(response.data));
    } catch (error) {
      handleError(error);
    }
  };

  const handleTabChange = async (categoryId, page = 1) => {
    setLoader(true);

    setSelectedTab(categoryId);
    try {
      const endpoint =
        categoryId === "tout"
          ? `/products?page=${page}`
          : `/products/categories/${categoryId}?page=${page}`;
      const response = await api.get(endpoint);
      dispatch(products(response.data));
      setLoader(false);
    } catch (error) {
      handleError(error);
    } finally {
      console.log("finally");
    }
  };

  const handleFilterProduct = async () => {
    // console.log("search", search);
    setLoader(true);

    try {
      if (search && search.length > 3) {
        const response = await api.get(`/products/${search}`);
        dispatch(
          products({
            products: response.data,
            page: 1,
            totalPages: 1,
          })
        );
        setLoader(false);
      } else if (search && search.length === 0) await productLoader(1);
    } catch (error) {
      handleError(error);
    } finally {
      return;
    }
  };
  const handlePageChange = async (page) => {
    if (page !== currentPage) {
      await productLoader(page);
    }
  };

  useEffect(() => {
    getCategories();
    productLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      handleFilterProduct();
    }, 300);
    return () => clearTimeout(debounceTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const tabsData = [
    { label: "Tout", value: "tout", icon: Bars3BottomLeftIcon },
    ...dataCategories.map(({ name, id }) => ({
      label: name,
      value: id,
      icon: Bars3BottomLeftIcon,
    })),
  ];

  return (
    <div className="mx-auto max-w-screen-lg">
      <div
        className="relative h-56 rounded-b-lg bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${decouvreBg})` }}
      >
        <div className="px-4 pt-8 pb-10">
          <div className="absolute inset-x-0 -bottom-10 mx-auto w-20 h-20 rounded-full border-8 border-green shadow-lg bg-white">
            <span className="absolute right-0 h-3 w-3 rounded-full bg-green-500 ring-2 ring-green-300 ring-offset-2"></span>
            <svg
              className="mx-auto mt-4 w-10"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 640 512"
            >
              <path d="M36.8 192l566.3 0c20.3 0 36.8-16.5 36.8-36.8c0-7.3-2.2-14.4-6.2-20.4L558.2 21.4C549.3 8 534.4 0 518.3 0L121.7 0c-16 0-31 8-39.9 21.4L6.2 134.7c-4 6.1-6.2 13.2-6.2 20.4C0 175.5 16.5 192 36.8 192zM64 224l0 160 0 80c0 26.5 21.5 48 48 48l224 0c26.5 0 48-21.5 48-48l0-80 0-160-64 0 0 160-192 0 0-160-64 0zm448 0l0 256c0 17.7 14.3 32 32 32s32-14.3 32-32l0-256-64 0z" />
            </svg>
          </div>
        </div>
      </div>
      <div className="my-4 sm:my-6 md:my-8 lg:my-10 xl:my-12">
        <label className="input input-bordered w-full max-w-xs flex items-center gap-2">
          <input
            type="text"
            className="grow bg-white"
            placeholder="Recherche"
            onChange={(e) => setSearch(e.target.value)}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="h-4 w-4 opacity-70"
          >
            <path
              fillRule="evenodd"
              d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
              clipRule="evenodd"
            />
          </svg>
        </label>
      </div>

      {/* 📱 Mobile: Menu Toggle */}
      <button
        className="sm:hidden flex items-center gap-2 px-4 py-2 bg-white ml-6 rounded-md  hover:bg-gray-100 transition"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Menu className="w-5 h-5" />
        <span>Menu</span>
      </button>

      <Tabs value={selectedTab}>
        {isOpen && (
          <TabsHeader className="flex flex-col items-start">
            {tabsData.map(({ label, value, icon }) => (
              <Tab
                key={value}
                value={value}
                onClick={() => handleTabChange(value)}
              >
                <div className="flex items-center gap-2">
                  {React.createElement(icon, { className: "w-5 h-5" })}
                  {label}
                </div>
              </Tab>
            ))}
          </TabsHeader>
        )}

        <TabsHeader className="hidden sm:flex items-center gap-4">
          {tabsData.map(({ label, value, icon }) => (
            <Tab
              key={value}
              value={value}
              onClick={() => handleTabChange(value)}
            >
              <div className="flex items-center gap-2">
                {React.createElement(icon, { className: "w-5 h-5" })}
                {label}
              </div>
            </Tab>
          ))}
        </TabsHeader>

        <TabsBody>
          {loader ? (
            <div className=" bg-white flex flex-col items-center justify-center w-2/4 p-12 rounded-lg my-12 mx-auto">
              <span className="loading loading-bars loading-lg text-success"></span>
              <p className='mt-4'>Patientez</p>
            </div>
          ) : listProducts.length > 0 ? (
            tabsData.map(({ value }) => (
              <TabPanel key={value} value={value} className="sm:px-8 md:px-0">
                <Categories listItem={listProducts} />
                {value === "tout" && (
                  <Paginate
                    listproductLength={{ page: currentPage, totalPages }}
                    handlePage={handlePageChange}
                  />
                )}
              </TabPanel>
            ))
          ) : (
            <div className="bg-white my-12 p-12 text-center text-gray-700 text-2xl rounded-md">
              Pas d'information enregistré
            </div>
          )}
        </TabsBody>
      </Tabs>
    </div>
  );
}

export default Product;
